import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCampaignComments,
  addCampaignComment,
  deleteCampaignComment,
  type CommentStage,
  type CommentType,
  type CampaignComment,
  type CommentMention,
  type MentionInput,
} from '../api/campaigns';
import { getCurrentUser } from '../api/users';
import { usePermissions } from '../hooks/usePermissions';
import { useTribute, type MentionData } from '../hooks/useTribute';
import { formatRelativeTime } from '../utils/date';

interface CommentThreadProps {
  campaignId: string;
  stage: CommentStage;
}

const STAGE_LABELS: Record<CommentStage, string> = {
  general: 'Comments',
  preview: 'Preview Feedback',
  asset: 'Asset Feedback',
};

/**
 * Render comment text with @mentions highlighted as blue styled spans.
 */
function renderCommentText(text: string, mentions: CommentMention[] | undefined): React.ReactNode {
  if (!mentions || mentions.length === 0) {
    return text;
  }

  // Build set of mentioned names for matching
  // Sort by descending length so longer names match before their prefixes (e.g. "Anna" before "Ann")
  const mentionNames = mentions
    .map((m) => m.name)
    .filter((name): name is string => name !== null && name !== undefined)
    .sort((a, b) => b.length - a.length);

  if (mentionNames.length === 0) {
    return text;
  }

  // Build regex to find @Name patterns — escape special regex chars in names
  const escaped = mentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`@(${escaped.join('|')})`, 'g');

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Add highlighted mention
    parts.push(
      <span key={key++} className="text-blue-600 font-medium bg-blue-50 rounded px-0.5">
        @{match[1]}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
}

export function CommentThread({ campaignId, stage }: CommentThreadProps) {
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState<CommentType>('general');
  const [mentions, setMentions] = useState<MentionData[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const queryClient = useQueryClient();
  const stageLabel = STAGE_LABELS[stage];

  // Tribute.js hook for @mention autocomplete
  const handleMentionsChange = useCallback((newMentions: MentionData[]) => {
    setMentions(newMentions);
  }, []);

  const handleTributeTextChange = useCallback((value: string) => {
    setNewComment(value);
  }, []);

  const { textareaRef, clearMentions, syncMentionsWithText, isMenuOpen } = useTribute({
    onMentionsChange: handleMentionsChange,
    onTextChange: handleTributeTextChange,
    attachDep: isExpanded,
  });

  // Fetch current user to determine ownership
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: getCurrentUser,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Read-only roles can follow a thread but not add to it. Defaults to false
  // while permissions load so the composer never flashes into view for a
  // viewer; the API refuses them regardless.
  const { data: permissions } = usePermissions();
  const canComment = permissions?.can_comment ?? false;

  const currentUserEmail = currentUser?.email;

  // Fetch comments for this stage
  const { data, isLoading } = useQuery({
    queryKey: ['campaign-comments', campaignId, stage],
    queryFn: () => getCampaignComments(campaignId, stage),
  });

  const comments = data?.comments ?? [];

  // Add comment mutation — sends mentions alongside the comment
  const addMutation = useMutation({
    mutationFn: ({
      text,
      type,
      mentionList,
    }: {
      text: string;
      type: CommentType;
      mentionList: MentionInput[];
    }) => addCampaignComment(campaignId, stage, text, type, mentionList),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-comments', campaignId, stage] });
      setNewComment('');
      setCommentType('general');
      clearMentions();
    },
  });

  // Delete comment mutation
  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => deleteCampaignComment(campaignId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-comments', campaignId, stage] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      addMutation.mutate({
        text: newComment.trim(),
        type: commentType,
        mentionList: mentions,
      });
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewComment(value);
    syncMentionsWithText(value);
  };

  const handleDelete = (commentId: string) => {
    if (window.confirm('Delete this comment?')) {
      deleteMutation.mutate(commentId);
    }
  };

  const canDelete = (comment: CampaignComment) => {
    // A viewer can still own comments authored before their role changed, and
    // the API refuses to delete them, so don't offer a control that 403s.
    return canComment && comment.can_delete;
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      {/* Header - clickable to expand/collapse */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            {stageLabel}
            {comments.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                {comments.length}
              </span>
            )}
          </span>
        </div>
        {!isExpanded && comments.length > 0 && (
          <span className="text-xs text-gray-500 truncate max-w-[200px]">
            Latest: "{comments[comments.length - 1]?.comment_text.slice(0, 30)}
            {comments[comments.length - 1]?.comment_text.length > 30 ? '...' : ''}"
          </span>
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 bg-white rounded-b-lg">
          {/* Comments list */}
          {isLoading ? (
            <div className="py-4 text-center text-sm text-gray-500">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="py-4 text-center text-sm text-gray-500">
              No comments yet. Be the first to add feedback.
            </div>
          ) : (
            <div className="space-y-3 mt-3 max-h-60 overflow-y-auto">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="bg-gray-50 rounded-lg border border-gray-200 p-3 group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      {comment.comment_type === 'rejection' && (
                        <span className="inline-block mb-1 px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                          Rejection
                        </span>
                      )}
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {renderCommentText(comment.comment_text, comment.mentions)}
                      </p>
                    </div>
                    {canDelete(comment) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(comment.id)}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-red-500 transition-opacity p-1"
                        title="Delete comment"
                        aria-label="Delete comment"
                        disabled={deleteMutation.isPending}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-medium">
                      {comment.author_email === currentUserEmail
                        ? 'You'
                        : comment.author_name || comment.author_email.split('@')[0]}
                    </span>
                    <span>-</span>
                    <span>{formatRelativeTime(comment.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add comment form — hidden entirely for read-only roles */}
          {!canComment ? (
            <p className="mt-3 text-xs text-gray-400">
              Your role has view-only access to comments.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="mt-3">
              {/* Comment type toggle — only shown for asset and preview stages */}
              {stage !== 'general' && (
                <div role="group" aria-label="Comment type" className="flex gap-1.5 mb-2">
                  <button
                    type="button"
                    aria-pressed={commentType === 'general'}
                    onClick={() => setCommentType('general')}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      commentType === 'general'
                        ? 'bg-gray-800 text-white border-gray-800'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    General
                  </button>
                  <button
                    type="button"
                    aria-pressed={commentType === 'rejection'}
                    onClick={() => setCommentType('rejection')}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      commentType === 'rejection'
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-red-600 border-red-300 hover:border-red-400'
                    }`}
                  >
                    Rejection
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  ref={textareaRef}
                  value={newComment}
                  onChange={handleTextChange}
                  placeholder="Add a comment... Use @ to mention someone"
                  aria-label="Add a comment"
                  className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400 resize-none"
                  maxLength={2000}
                  rows={2}
                  disabled={addMutation.isPending}
                  onKeyDown={(e) => {
                    // Submit on Enter (without Shift), let Shift+Enter add newline
                    if (e.key === 'Enter' && !e.shiftKey) {
                      // Don't submit if tribute menu is active (user is selecting a mention)
                      if (isMenuOpen()) {
                        return; // Let tribute handle the Enter key
                      }
                      e.preventDefault();
                      if (newComment.trim()) {
                        addMutation.mutate({
                          text: newComment.trim(),
                          type: commentType,
                          mentionList: mentions,
                        });
                      }
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || addMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors self-end"
                >
                  {addMutation.isPending ? 'Sending...' : 'Send'}
                </button>
              </div>
              {addMutation.isError && (
                <p className="mt-2 text-xs text-red-600">
                  Failed to add comment. Please try again.
                </p>
              )}
            </form>
          )}
        </div>
      )}
    </div>
  );
}
